import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Settings,
  BarChart3,
  FileText,
  UserCog,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Bell,
  CreditCard,
  Shield,
  Database,
  Activity,
  Calendar,
  Mail,
  Globe,
  Zap,
  HelpCircle,
  Search,
  Upload,
  Download,
  Archive,
  Monitor,
  Smartphone,
  Key,
  IndentIcon,
  FormInput,
  LogOut,
  Building,
  Landmark,
  Gift,
  User,
  Gamepad,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import { useCurrentAdmin, useUserRole } from '@/hooks/redux';
import { ACCOUNT_TYPES_NEW } from '@/types';
import { useLogoutMutation } from '@/store/api/authApi';
import { useDispatch } from 'react-redux';
import { clearAuth } from '@/store';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  badge?: string;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  {
    name: 'User Management',
    icon: Users,
    adminOnly: true,
    children: [
      { name: 'Member', href: `/users/${ACCOUNT_TYPES_NEW.MEMBER}`, icon: Users },
      // { name: 'Community', href: `/users/${ACCOUNT_TYPES_NEW.COMMUNITY}`, icon: Users },
      // { name: 'Partner', href: `/users/${ACCOUNT_TYPES_NEW.PARTNER}`, icon: Users },
      { name: 'Tribe', href: `/users/${ACCOUNT_TYPES_NEW.TRIBE}`, icon: Users },
    ],
  },
  { name: 'Contestant', href: '/contestant', icon: Gamepad },
  { name: 'Community Partners', href: '/community-partners', icon: Home },
  { name: 'Voting', href: '/voting', icon: BarChart3 },

  // {
  //   name: 'Transactions',
  //   icon: FileText,
  //   children: [
  //     { name: 'Deposit Request', href: '/deposit-request', icon: FileText },
  //     { name: 'Withdraw Request', href: '/withdraw-request', icon: Upload },
  //   ],
  // },
  // { name: 'Trustee KYC', href: '/trustee-kyc-request', icon: IndentIcon },
  // { name: 'Master RWA Form', href: '/master-rwa-form', icon: FormInput },
  // { name: 'Asset List', href: '/asset-list', icon: Building },
  // { name: 'Coupon', href: '/coupon-page', icon: Gift },
  // { name: 'Vendor List', href: '/vendors-page', icon: User },
  { name: 'Security', href: '/change-password', icon: Shield },
  { name: 'Profile', href: '/profile', icon: UserCog },
];

interface NavItemComponentProps {
  item: NavItem;
  isOpen: boolean;
  level?: number;
}

function NavItemComponent({ item, isOpen, level = 0 }: NavItemComponentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const userRole = useUserRole();
  const adminDetail = useCurrentAdmin();

  const isAdmin = userRole === 'admin';

  // Don't render admin-only items for non-admin users
  if (item.adminOnly && !isAdmin) {
    return null;
  }

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href ? location.pathname === item.href : false;
  const hasActiveChild = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  );

  // Auto-expand if has active child
  React.useEffect(() => {
    if (hasActiveChild && isOpen) {
      setIsExpanded(true);
    }
  }, [hasActiveChild, isOpen]);

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const Icon = item.icon;
  const indent = level * 16;

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={handleClick}
          className={cn(
            'flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            isActive || hasActiveChild
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            !isOpen && 'justify-center'
          )}
          style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
          title={!isOpen ? item.name : undefined}
        >
          <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
          {isOpen && (
            <>
              <span className="flex-1 text-left">{item.name}</span>
              {item.badge && (
                <span className="ml-2 inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium">
                  {item.badge}
                </span>
              )}
              {isExpanded ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </>
          )}
        </button>

        {/* Submenu */}
        {hasChildren && isExpanded && isOpen && (
          <div className="mt-1 space-y-1">
            {item.children!.map((child) => (
              <NavItemComponent key={child.name} item={child} isOpen={isOpen} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Regular nav item
  return (
    <NavLink
      to={item.href!}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          !isOpen && 'justify-center'
        )
      }
      style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
      title={!isOpen ? item.name : undefined}
    >
      <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
      {isOpen && (
        <>
          <span>{item.name}</span>
          {item.badge && (
            <span className="ml-auto inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium">
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const adminDetail = useCurrentAdmin();
  const userRole = useUserRole();
  const [logoutApi] = useLogoutMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logoutApi().unwrap();
    } catch (error) {
      console.error('Logout API failed:', error);
    } finally {
      dispatch(clearAuth());
      navigate('/login', { replace: true });
    }
  };

  return (
    <>
      {/* Sidebar backdrop for mobile */}
      {isOpen && <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={onToggle} />}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col bg-card border-r transition-all duration-300 overflow-hidden',
          isOpen ? 'w-64 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div className="flex h-16 items-center border-b px-4">
          <div className={cn('flex items-center', isOpen ? 'w-full' : 'justify-center')}>
            <div className="flex h-8 w-8 items-center justify-center rounded-lg  text-primary-foreground font-bold">
              <img src="/smallLogo.svg" alt="" />
            </div>
            {isOpen && (
              <div className="ml-3 flex-1">
                <span className="text-lg font-semibold">Admin</span>
              </div>
            )}
          </div>

          {/* Toggle button - desktop only */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn('hidden lg:flex h-6 w-6', isOpen ? 'ml-auto' : 'fixed left-2 top-3 z-40')}
          >
            {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-2 overflow-y-auto sidebar">
          {navigation.map((item) => (
            <NavItemComponent key={item.name} item={item} isOpen={isOpen} />
          ))}
        </nav>

        {/* User info */}
        {isOpen && adminDetail && (
          <div className="border-t p-4">
            <div className="flex items-center">
              {adminDetail?.profileImage ? (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <img
                    src={adminDetail?.profileImage}
                    alt="Admin profile"
                    className="h-full w-full object-cover rounded-full"
                  />
                </div>
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm">
                  {adminDetail?.firstname.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{adminDetail?.firstname}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {userRole?.toLowerCase()} • {adminDetail?.email}
                </p>
              </div>
            </div>

            {/* Quick actions */}
            <div className="mt-3 flex space-x-2">
              <Button onClick={handleLogout} variant="outline" size="sm" className="flex-1">
                <LogOut className="h-3 w-3 mr-1" />
                Logout
              </Button>
            </div>
          </div>
        )}

        {/* Collapsed user indicator */}
        {!isOpen && adminDetail && (
          <div className="border-t p-2">
            <div className="flex justify-center">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm">
                {adminDetail?.firstname.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
